import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SajuResult } from '@/utils/sajuCalculations';
import { getMonthIljin, type MonthDayData, LUCK_STYLES, type LuckLevel, getMonthIljinEn, LUCK_STYLES_EN } from '@/utils/iljinCalculations';
import { ELEMENT_COLORS, ELEMENT_BG_COLORS } from '@/utils/sajuCalculations';

interface Props {
  result: SajuResult;
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const WEEKDAYS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function DayCell({ data, onSelect, isEn }: { data: MonthDayData; onSelect: (d: MonthDayData) => void; isEn: boolean }) {
  const style = isEn ? LUCK_STYLES_EN[data.luck.level] : LUCK_STYLES[data.luck.level];
  const isGood = data.luck.level === 'great' || data.luck.level === 'good';
  const isBad  = data.luck.level === 'caution' || data.luck.level === 'bad';

  return (
    <button
      onClick={() => onSelect(data)}
      className="flex flex-col items-center py-2 rounded-xl cursor-pointer transition-all hover:scale-105 active:scale-95 relative"
      style={{ backgroundColor: data.isToday ? '#1F2937' : style.bg || 'transparent' }}
    >
      <span
        className="text-sm font-bold leading-none mb-1"
        style={{ color: data.isToday ? '#fff' : data.weekday === 0 ? '#EF4444' : data.weekday === 6 ? '#3B82F6' : '#1F2937' }}
      >
        {data.day}
      </span>
      <span
        className="text-xs leading-none mb-1.5"
        style={{ color: data.isToday ? '#D1D5DB' : ELEMENT_COLORS[data.ganzhi.stemElement], fontFamily: "'Noto Serif KR', serif", fontSize: '10px' }}
      >
        {data.ganzhi.stemKor}{data.ganzhi.branchKor}
      </span>
      {data.luck.level !== 'neutral' && (
        <span
          className="text-xs font-black leading-none"
          style={{ color: data.isToday ? (isGood ? '#6EE7B7' : '#FCA5A5') : style.dot, fontSize: '10px' }}
        >
          {isGood ? '●' : '▲'}
        </span>
      )}
      {data.luck.level === 'neutral' && (
        <span className="text-gray-300 leading-none" style={{ fontSize: '10px' }}>·</span>
      )}
    </button>
  );
}

function LuckDetail({ data, onClose, isEn }: { data: MonthDayData; onClose: () => void; isEn: boolean }) {
  const style = isEn ? LUCK_STYLES_EN[data.luck.level] : LUCK_STYLES[data.luck.level];
  const stemBg = ELEMENT_BG_COLORS[data.ganzhi.stemElement];
  const stemColor = ELEMENT_COLORS[data.ganzhi.stemElement];
  const branchBg = ELEMENT_BG_COLORS[data.ganzhi.branchElement];
  const branchColor = ELEMENT_COLORS[data.ganzhi.branchElement];

  const levelMessages: Record<LuckLevel, string> = isEn ? {
    great: 'Today is an excellent day! Important decisions, new beginnings, contracts — all good!',
    good:  'Today is a good day. Go ahead and push forward with what you wanted to do.',
    neutral:'An ordinary day. Keep your routine steady without overdoing it.',
    caution:'Today requires a bit of caution. Consider postponing major decisions.',
    bad:   'Make today a rest day. Focus on finishing things rather than starting new ones.',
  } : {
    great: '오늘은 최고의 날이에요! 중요한 결정, 시작, 계약 모두 OK!',
    good:  '오늘은 좋은 날이에요. 하고 싶었던 일을 추진필보세요.',
    neutral:'평범한 하루예요. 무리하지 않고 일상을 잘 유지하세요.',
    caution:'오늘은 조금 주의가 필요한 날이에요. 중요한 결정은 미뤄보세요.',
    bad:   '오늘은 쉬어가는 날로 정하세요. 새로운 시작보다 마무리에 집중해요.',
  };

  const wdays = isEn ? WEEKDAYS_EN : WEEKDAYS;
  const labelStem = isEn ? 'Heavenly Stem' : '천간';
  const labelBranch = isEn ? 'Earthly Branch' : '지지';
  const labelTodayIljin = isEn ? "Today's Iljin" : '오늘의 일진';
  const btnConfirm = isEn ? 'OK' : '확인';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative bg-white w-full max-w-md rounded-t-3xl p-6 pb-10" onClick={e => e.stopPropagation()}>
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">
              {data.year}{isEn ? '. ' : '년 '}{data.month}{isEn ? '. ' : '월 '}{data.day}{isEn ? '' : '일'} ({wdays[data.weekday]})
            </p>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: style.dot }}>
                {style.label}
              </span>
              <p className="text-sm font-bold text-gray-900">{labelTodayIljin}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500 text-sm" />
          </button>
        </div>

        <div className="flex gap-3 justify-center mb-5">
          <div className="flex flex-col items-center gap-1.5">
            <div className="w-16 h-16 rounded-2xl flex flex-col items-center justify-center" style={{ backgroundColor: stemBg }}>
              <span className="text-2xl font-bold" style={{ color: stemColor, fontFamily: "'Noto Serif KR', serif" }}>{data.ganzhi.stem}</span>
              <span className="text-xs" style={{ color: stemColor }}>{data.ganzhi.stemKor}</span>
            </div>
            <span className="text-xs text-gray-400">{labelStem} · {data.ganzhi.stemElement}</span>
          </div>
          <div className="flex flex-col items-center gap-1.5">
            <div className="w-16 h-16 rounded-2xl flex flex-col items-center justify-center" style={{ backgroundColor: branchBg }}>
              <span className="text-2xl font-bold" style={{ color: branchColor, fontFamily: "'Noto Serif KR', serif" }}>{data.ganzhi.branch}</span>
              <span className="text-xs" style={{ color: branchColor }}>{data.ganzhi.branchKor}</span>
            </div>
            <span className="text-xs text-gray-400">{labelBranch} · {data.ganzhi.branchElement}</span>
          </div>
        </div>

        <div className="rounded-2xl p-4 mb-4" style={{ backgroundColor: style.bg || '#F9FAFB' }}>
          <p className="text-sm font-bold mb-1.5" style={{ color: style.text }}>{levelMessages[data.luck.level]}</p>
          {data.luck.reasons.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {data.luck.reasons.slice(0, 3).map((r, i) => (
                <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-white/70 font-medium" style={{ color: style.text }}>{r}</span>
              ))}
            </div>
          )}
        </div>

        <button onClick={onClose} className="w-full bg-gray-900 text-white font-bold py-3.5 rounded-2xl cursor-pointer whitespace-nowrap hover:bg-gray-800 transition-all">
          {btnConfirm}
        </button>
      </div>
    </div>
  );
}

export default function IljinCalendar({ result }: Props) {
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [selected, setSelected] = useState<MonthDayData | null>(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const days = isEn ? getMonthIljinEn(year, month, result) : getMonthIljin(year, month, result);
  const firstWeekday = new Date(year, month - 1, 1).getDay();

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const greatDays = days.filter(d => d.luck.level === 'great').length;
  const goodDays  = days.filter(d => d.luck.level === 'good').length;
  const badDays   = days.filter(d => d.luck.level === 'caution' || d.luck.level === 'bad').length;

  const wdays = isEn ? WEEKDAYS_EN : WEEKDAYS;
  const statsLabels = isEn
    ? [{ label: 'Great', count: greatDays, color: '#10B981', bg: '#D1FAE5' }, { label: 'Good', count: goodDays, color: '#34D399', bg: '#ECFDF5' }, { label: 'Caution', count: badDays, color: '#EF4444', bg: '#FEF2F2' }]
    : [{ label: '대길일', count: greatDays, color: '#10B981', bg: '#D1FAE5' }, { label: '길일', count: goodDays, color: '#34D399', bg: '#ECFDF5' }, { label: '주의일', count: badDays, color: '#EF4444', bg: '#FEF2F2' }];
  const legendItems = isEn
    ? [{ level: 'great' as const, icon: '●', text: 'Great' }, { level: 'good' as const, icon: '●', text: 'Good' }, { level: 'neutral' as const, icon: '·', text: 'Neutral' }, { level: 'caution' as const, icon: '▲', text: 'Caution' }, { level: 'bad' as const, icon: '▲', text: 'Bad' }]
    : [{ level: 'great' as const, icon: '●', text: '대길' }, { level: 'good' as const, icon: '●', text: '길' }, { level: 'neutral' as const, icon: '·', text: '평' }, { level: 'caution' as const, icon: '▲', text: '주의' }, { level: 'bad' as const, icon: '▲', text: '흉' }];
  const styles = isEn ? LUCK_STYLES_EN : LUCK_STYLES;

  return (
    <div ref={ref} className="bg-white border border-gray-100 rounded-2xl p-5 mb-6 transition-all duration-700" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}>
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-xl">
          <i className="ri-calendar-2-line text-amber-500 text-sm" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-gray-900">{isEn ? 'Daily Iljin Calendar' : '일진(日辰) 달력'}</h3>
          <p className="text-xs text-gray-400">{isEn ? 'Analyzing compatibility between your Saju and each date' : '나의 사주와 각 날짜의 궁합을 분석했어요'}</p>
        </div>
      </div>

      <div className="flex gap-2 mb-5">
        {statsLabels.map(s => (
          <div key={s.label} className="flex-1 rounded-xl py-2.5 text-center" style={{ backgroundColor: s.bg }}>
            <p className="text-lg font-black" style={{ color: s.color }}>{s.count}</p>
            <p className="text-xs font-medium" style={{ color: s.color }}>{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer hover:bg-gray-200 transition-all">
          <i className="ri-arrow-left-s-line text-gray-600 text-base" />
        </button>
        <p className="text-sm font-bold text-gray-900">{year}{isEn ? '. ' : '년 '}{month}{isEn ? '' : '월'}</p>
        <button onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer hover:bg-gray-200 transition-all">
          <i className="ri-arrow-right-s-line text-gray-600 text-base" />
        </button>
      </div>

      <div className="grid grid-cols-7 mb-1">
        {wdays.map((w, i) => (
          <div key={w} className="text-center py-1">
            <span className={`text-xs font-bold ${i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-400'}`}>{w}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {Array.from({ length: firstWeekday }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}
        {days.map(d => (
          <DayCell key={d.day} data={d} onSelect={setSelected} isEn={isEn} />
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-3 justify-center">
        {legendItems.map(l => (
          <div key={l.level} className="flex items-center gap-1">
            <span className="text-sm font-bold" style={{ color: styles[l.level].dot }}>{l.icon}</span>
            <span className="text-xs text-gray-500">{l.text}</span>
          </div>
        ))}
        <div className="flex items-center gap-1">
          <div className="w-3.5 h-3.5 rounded-sm bg-gray-900" />
          <span className="text-xs text-gray-500">{isEn ? 'Today' : '오늘'}</span>
        </div>
      </div>

      {selected && <LuckDetail data={selected} onClose={() => setSelected(null)} isEn={isEn} />}
    </div>
  );
}
